/**
 * Input Component
 * Text input with variants
 */

import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { transformStyleProps, splitProps, stylePropsKeys } from '../system/style-props';
import { useTheme } from '../theme';

/**
 * Base styled input
 */
const StyledInput = styled('input', {
  shouldForwardProp: (prop) => {
    if (stylePropsKeys.includes(prop)) return false;
    if (['as', 'sx', '__css', 'variant', 'size', 'isDisabled', 
         'isInvalid', 'isReadOnly', 'isRequired', 'focusBorderColor',
         'errorBorderColor'].includes(prop)) return false;
    return true;
  },
})(({ theme, ...props }) => {
  const styles = [];

  if (props.__css) {
    const cssResult = typeof props.__css === 'function'
      ? props.__css({ theme, colorMode: 'light', ...props })
      : props.__css;
    styles.push(transformStyleProps(cssResult, theme));
  }

  const [styleProps] = splitProps(props);
  styles.push(transformStyleProps(styleProps, theme));

  if (props.sx) {
    const sxResult = typeof props.sx === 'function'
      ? props.sx({ theme, ...props })
      : props.sx;
    styles.push(transformStyleProps(sxResult, theme));
  }

  return styles;
});

/**
 * Input Component
 * 
 * @example
 * <Input placeholder="Enter text" variant="main" />
 * <Input isInvalid={hasError} />
 */
export const Input = forwardRef(({
  as,
  variant = 'outline',
  size = 'md',
  isDisabled = false,
  isInvalid = false,
  isReadOnly = false,
  isRequired = false,
  focusBorderColor = 'brand.500',
  errorBorderColor = 'red.500',
  ...props
}, ref) => {
  const theme = useTheme();
  const inputStyles = theme?.components?.Input || {};
  
  // Get base styles
  const baseStyle = inputStyles.baseStyle?.field || {};
  
  // Get size styles
  const sizeStyle = inputStyles.sizes?.[size]?.field || {};
  
  // Get variant styles
  const variantStyle = typeof inputStyles.variants?.[variant] === 'function'
    ? (inputStyles.variants[variant]({ colorMode: 'light' })?.field || {})
    : (inputStyles.variants?.[variant]?.field || {});

  // Merge all styles
  const mergedStyles = {
    width: '100%',
    minWidth: 0,
    outline: 0,
    position: 'relative',
    appearance: 'none',
    transition: 'all 0.2s',
    borderWidth: '1px',
    borderStyle: 'solid',
    ...baseStyle,
    ...sizeStyle,
    ...variantStyle,
    ...(isDisabled ? {
      opacity: 0.4,
      cursor: 'not-allowed',
    } : {}),
    ...(isInvalid ? {
      borderColor: errorBorderColor,
    } : {}),
  };

  // Remove pseudo-props
  const cleanStyles = { ...mergedStyles };
  ['_hover', '_focus', '_disabled', '_invalid', '_placeholder'].forEach(key => {
    delete cleanStyles[key];
  });

  return (
    <StyledInput
      ref={ref}
      as={as}
      disabled={isDisabled}
      readOnly={isReadOnly}
      required={isRequired}
      aria-invalid={isInvalid}
      aria-required={isRequired}
      aria-readonly={isReadOnly}
      aria-disabled={isDisabled}
      data-invalid={isInvalid || undefined}
      data-disabled={isDisabled || undefined}
      data-readonly={isReadOnly || undefined}
      __css={cleanStyles}
      {...props}
    />
  );
});

Input.displayName = 'Input';

export default Input;
